/* SectionMedia — reusable public renderer.
 *
 * Usage: <SectionMedia sectionKey="home-mission" className="..." />
 *
 * Reads /api/public/site-sections/{key}, then renders:
 *   • Single mode → <picture> with responsive srcset from all 4 variants
 *   • Slideshow mode → fade or slide transition, optional autoplay,
 *     touch/swipe on mobile, keyboard arrows, dots, nav arrows.
 *
 * Fail-safe: if the API returns 404 or the section has no media, we
 * render a subtle placeholder rather than a broken image. Sections
 * with a `fallback` prop use that image instead of the placeholder.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SERVE = `${BACKEND_URL}/api/media/serve`;

const buildSrcSet = (media) => {
  // <img srcset> ordered from smallest → largest so mobile picks
  // mobile.webp on cellular.
  const parts = [];
  const V = media.variants || {};
  if (V.mobile) parts.push(`${SERVE}/${media.id}/mobile ${V.mobile.width}w`);
  if (V.tablet) parts.push(`${SERVE}/${media.id}/tablet ${V.tablet.width}w`);
  if (V.desktop) parts.push(`${SERVE}/${media.id}/desktop ${V.desktop.width}w`);
  return parts.join(', ');
};

const ResponsiveImg = ({ media, className, sizes, priority }) => (
  <img
    src={`${SERVE}/${media.id}/desktop`}
    srcSet={buildSrcSet(media)}
    sizes={sizes || '(max-width: 640px) 640px, (max-width: 1024px) 1024px, 1920px'}
    alt={media.alt_text || media.title || ''}
    className={className}
    loading={priority ? 'eager' : 'lazy'}
    decoding="async"
  />
);

const SectionMedia = ({ sectionKey, className = '', fallback = null,
                          objectFit = 'cover', priority = false }) => {
  const [config, setConfig] = useState(null);
  const [idx, setIdx] = useState(0);
  const [error, setError] = useState(false);
  const touchStartX = useRef(null);
  const intervalRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND_URL}/api/public/site-sections/${sectionKey}`)
      .then((r) => { if (alive) setConfig(r.data); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [sectionKey]);

  const media = config?.media || [];
  const count = media.length;
  const isSlideshow = config?.display_mode === 'slideshow' && count > 1;

  const advance = useCallback((dir = 1) => {
    setIdx((cur) => {
      const next = cur + dir;
      if (config?.loop === false) {
        if (next < 0) return 0;
        if (next >= count) return count - 1;
      }
      return ((next % count) + count) % count;
    });
  }, [count, config?.loop]);

  // Autoplay — respects `autoplay` toggle + `autoplay_interval_ms` +
  // pauses on tab hidden.
  useEffect(() => {
    if (!isSlideshow || !config?.autoplay) return;
    intervalRef.current = setInterval(() => advance(1),
      config.autoplay_interval_ms || 5000);
    const onVis = () => {
      if (document.hidden) {
        clearInterval(intervalRef.current);
      } else {
        intervalRef.current = setInterval(() => advance(1),
          config.autoplay_interval_ms || 5000);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isSlideshow, config?.autoplay, config?.autoplay_interval_ms, advance]);

  // Keyboard nav when the slideshow has focus.
  useEffect(() => {
    if (!isSlideshow) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') advance(1);
      else if (e.key === 'ArrowLeft') advance(-1);
    };
    const el = containerRef.current;
    el?.addEventListener('keydown', onKey);
    return () => el?.removeEventListener('keydown', onKey);
  }, [isSlideshow, advance]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) advance(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  if (error || !config) {
    if (fallback) {
      return <img src={fallback} alt="" className={className}
        data-testid={`section-fallback-${sectionKey}`} />;
    }
    // No fallback + not yet loaded / 404 → render nothing so the mount
    // is invisible until an admin configures it. Callers who want a
    // placeholder can pass a fallback prop.
    return null;
  }
  if (count === 0) {
    if (fallback) {
      return <img src={fallback} alt="" className={className}
        data-testid={`section-fallback-${sectionKey}`} />;
    }
    return null;
  }

  // ─── SINGLE MODE ────────────────────────────────────────────────
  if (!isSlideshow) {
    const m = media[0];
    return (
      <ResponsiveImg media={m} priority={priority}
        className={`${className} object-${objectFit}`} />
    );
  }

  // ─── SLIDESHOW MODE ─────────────────────────────────────────────
  const useSlide = config.transition === 'slide';

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className={`relative overflow-hidden focus:outline-none ${className}`}
      data-testid={`section-slideshow-${sectionKey}`}>

      {useSlide ? (
        <div className="flex h-full transition-transform duration-700
                       ease-out"
          style={{ transform: `translateX(-${idx * 100}%)` }}
          data-testid="slideshow-track">
          {media.map((m) => (
            <div key={m.id} className="flex-shrink-0 w-full h-full">
              <ResponsiveImg media={m} priority={priority}
                className={`w-full h-full object-${objectFit}`} />
            </div>
          ))}
        </div>
      ) : (
        <>
          {media.map((m, i) => (
            <div key={m.id}
              className={`absolute inset-0 transition-opacity duration-700
                ease-out ${i === idx ? 'opacity-100' : 'opacity-0'}`}
              aria-hidden={i !== idx}
              data-testid={`slide-${i}`}>
              <ResponsiveImg media={m} priority={priority && i === 0}
                className={`w-full h-full object-${objectFit}`} />
            </div>
          ))}
        </>
      )}

      {/* Nav arrows */}
      {config.show_arrows && (
        <>
          <button onClick={() => advance(-1)}
            aria-label="Previous image"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10
                      bg-black/40 hover:bg-black/60 text-white rounded-full
                      w-8 h-8 flex items-center justify-center transition"
            data-testid={`prev-${sectionKey}`}>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => advance(1)}
            aria-label="Next image"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10
                      bg-black/40 hover:bg-black/60 text-white rounded-full
                      w-8 h-8 flex items-center justify-center transition"
            data-testid={`next-${sectionKey}`}>
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      )}

      {/* Pagination dots */}
      {config.show_dots && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10
                       flex gap-1.5" data-testid={`dots-${sectionKey}`}>
          {media.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)}
              aria-label={`Go to image ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? 'w-6 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80'
              }`}
              data-testid={`dot-${sectionKey}-${i}`} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SectionMedia;
